import type { SignalClient } from "../signalClient";
import type { IceServer } from "../signalMessages";
import type { Transport } from "./transport";

interface SignalData {
  readonly description?: RTCSessionDescriptionInit;
  readonly candidate?: RTCIceCandidateInit;
}

const CONNECT_TIMEOUT_MS = 20_000;

/**
 * Канал WebRTC до соперника: DataChannel без порядка и повторов (как UDP), описания и адреса — через сводник.
 * Создатель комнаты делает предложение, гость отвечает.
 */
export function connectPeer(
  signal: SignalClient,
  role: "host" | "guest",
  ice: readonly IceServer[],
): Promise<Transport> {
  const peer = new RTCPeerConnection({ iceServers: ice.map((server) => ({ ...server, urls: [server.urls].flat() })) });
  let handler: (bytes: Uint8Array) => void = () => undefined;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      peer.close();
      reject(new Error("Не удалось соединиться с соперником напрямую — нужен TURN"));
    }, CONNECT_TIMEOUT_MS);
    const ready = (channel: RTCDataChannel): void => {
      channel.binaryType = "arraybuffer";
      channel.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        handler(new Uint8Array(event.data));
      };
      channel.onopen = () => {
        clearTimeout(timer);
        resolve({
          send: (bytes) => {
            if (channel.readyState === "open") channel.send(bytes as Uint8Array<ArrayBuffer>);
          },
          onMessage: (next) => {
            handler = next;
          },
          close: () => {
            channel.close();
            peer.close();
          },
        });
      };
    };
    peer.onicecandidate = (event) => {
      if (event.candidate) signal.send({ t: "signal", data: { candidate: event.candidate.toJSON() } });
    };
    signal.on((message) => {
      if (message.t !== "signal") return;
      const data = message.data as SignalData;
      void (async () => {
        if (data.description) {
          await peer.setRemoteDescription(data.description);
          if (data.description.type === "offer") {
            await peer.setLocalDescription(await peer.createAnswer());
            signal.send({ t: "signal", data: { description: peer.localDescription?.toJSON() } });
          }
        } else if (data.candidate) {
          await peer.addIceCandidate(data.candidate);
        }
      })().catch((error: unknown) => {
        reject(error instanceof Error ? error : new Error(String(error)));
      });
    });
    if (role === "host") {
      ready(peer.createDataChannel("inputs", { ordered: false, maxRetransmits: 0 }));
      void (async () => {
        await peer.setLocalDescription(await peer.createOffer());
        signal.send({ t: "signal", data: { description: peer.localDescription?.toJSON() } });
      })();
    } else {
      peer.ondatachannel = (event) => {
        ready(event.channel);
      };
    }
  });
}
